import { useTranslation } from 'react-i18next'
import { ChevronRight, Lock } from 'lucide-react'
import type { MarketMatch, WinnerSelection } from '@brochure/shared'
import { Flap } from '@/components/ui/Flap'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ScoreFlaps } from '@/components/zambepong/ScoreFlaps'
import { formatOdds } from '@/lib/zambebet'
import { phaseKey, phaseParams } from '@/lib/zambepong-format'
import { cn } from '@/lib/utils'
import { ScrollingName } from '@/components/ui/ScrollingName'

/** Casa = mare, trasferta = corallo: gli stessi colori del tavolo e delle righe partita. */
export const SIDE_TONE = {
  home: { stripe: 'bg-sea', picked: 'border-sea bg-sea/15', soft: 'bg-sea/20 text-sea-ink' },
  away: { stripe: 'bg-coral', picked: 'border-coral bg-coral/15', soft: 'bg-coral/20 text-coral-ink' },
} as const

/**
 * Quota sulle palette del tabellone partenze: cambia valore e le cifre ricadono (`animate-flap` dentro `Flap`).
 * Il separatore decimale resta fuori dalle palette, come i due punti del punteggio.
 */
export function OddsFlaps({ odds, className, testId }: { odds: number; className?: string; testId?: string }) {
  const { i18n } = useTranslation()
  const text = formatOdds(odds, i18n.language)
  return (
    <span data-testid={testId} className={cn('inline-flex items-end gap-[0.06em] font-body font-semibold leading-none tabular-nums', className)}>
      <span className="sr-only">{text}</span>
      {text.split('').map((char, i) =>
        /\d/.test(char) ? <Flap key={i} char={char} /> : <span key={i} aria-hidden="true" className="px-[0.04em] opacity-60">{char}</span>,
      )}
    </span>
  )
}

type MarketCardProps = {
  tournamentId: string
  market: MarketMatch
  picked: WinnerSelection | null
  onPick: (selection: WinnerSelection) => void
  /** Falso dentro le sezioni già intitolate con la fase: l'etichetta sarebbe un doppione. */
  showPhase?: boolean
}

// Sotto i 360 px le caselle vanno su una riga intera sotto le squadre (vedi la riga in `MarketCard`).
const cellClass =
  'flex h-14 w-[3.25rem] max-[359px]:w-full flex-col items-center justify-center gap-1 rounded-lg border font-body transition-[transform,background-color,border-color] duration-200 ease-out-expo'

/**
 * Una partita come riga di palinsesto: le due squadre una sopra l'altra (striscia mare/corallo) e a destra le
 * caselle 1, 2 e "Altro +N". 1 e 2 entrano (o escono) dalla schedina; "Altro" porta alla pagina con tutti i mercati della
 * partita. A partita conclusa le caselle lasciano il posto al risultato.
 */
export function MarketCard({ tournamentId, market, picked, onPick, showPhase = true }: MarketCardProps) {
  const { t, i18n } = useTranslation()
  const { matchId, odds, blockedBy, status } = market
  const isLive = status === 'live'
  const isCompleted = status === 'completed'
  const hasScore = market.homeScore !== null && market.awayScore !== null
  const winner = isCompleted && hasScore ? (market.homeScore! > market.awayScore! ? 'home' : 'away') : null
  // Lucchetto solo quando il 1-2 è davvero chiuso a te (tua squadra, mercato chiuso): chi non gioca vede le quote.
  const isLocked = blockedBy === 'ownTeam' || blockedBy === 'closed'
  const reason = isLocked ? t(`zambebet.blocked.${blockedBy}`) : null

  const team = (selection: WinnerSelection) => {
    const { name } = selection === 'home' ? market.homeTeam : market.awayTeam
    return (
      <span className="flex min-w-0 items-center gap-2">
        <span aria-hidden="true" className={cn('h-4 w-1 shrink-0 rounded-full', SIDE_TONE[selection].stripe)} />
        <ScrollingName className={cn('min-w-0 flex-1 font-body text-sm', winner && winner !== selection ? 'text-paper/55' : 'font-semibold text-paper')}>
          {name}
        </ScrollingName>
      </span>
    )
  }

  const pickCell = (selection: WinnerSelection) => {
    const value = odds?.[selection] ?? null
    const { name } = selection === 'home' ? market.homeTeam : market.awayTeam
    const isPicked = picked === selection
    return (
      <button
        type="button"
        data-testid={`odds-${matchId}-${selection}`}
        disabled={!market.canBet || value === null}
        aria-pressed={isPicked}
        aria-label={value === null ? name : t('zambebet.markets.pick', { team: name, odds: formatOdds(value, i18n.language) })}
        onClick={() => onPick(selection)}
        className={cn(
          cellClass,
          'border-paper/15 bg-paper/5 enabled:hover:border-paper/45 enabled:active:scale-[0.95] disabled:cursor-not-allowed',
          isLocked && 'opacity-45',
          isPicked && SIDE_TONE[selection].picked,
        )}
      >
        <span className="text-[0.7rem] font-semibold leading-none text-paper/55">{selection === 'home' ? '1' : '2'}</span>
        {isLocked ? (
          <Lock className="size-3.5 text-paper/60" aria-hidden="true" />
        ) : value !== null ? (
          <OddsFlaps odds={value} className="text-sm text-paper" />
        ) : (
          <span className="text-sm leading-none text-paper/40">–</span>
        )}
      </button>
    )
  }

  return (
    <article
      data-testid={`market-${matchId}`}
      className={cn('rounded-xl border bg-paper/[0.04] px-3 py-2.5', isLive ? 'border-coral/60 shadow-card' : 'border-paper/12')}
    >
      {(showPhase || isLive) && (
        <header className="mb-2 flex min-h-6 items-center justify-between gap-3">
          <span className="font-body text-xs font-semibold text-paper/55">{showPhase && t(phaseKey(market.phase), phaseParams(market.phase))}</span>
          {isLive && (
            <span className="flex items-center gap-2.5">
              <span className="flex items-center gap-1 rounded-full bg-coral px-1.5 py-px font-body text-xs font-bold tracking-[0.02em] text-ink">
                <span className="size-1 rounded-full bg-ink motion-safe:animate-pulse" aria-hidden="true" />
                {t('zambePong.live')}
              </span>
              {hasScore && <ScoreFlaps home={market.homeScore} away={market.awayScore} className="text-sm text-paper" />}
            </span>
          )}
        </header>
      )}

      <div className="flex items-center gap-3 max-[359px]:flex-col max-[359px]:items-stretch max-[359px]:gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {team('home')}
          {team('away')}
        </div>
        {isCompleted ? (
          <span className="shrink-0 rounded-lg bg-paper/5 px-2 py-1">
            <ScoreFlaps home={market.homeScore} away={market.awayScore} className="text-base text-paper" />
          </span>
        ) : (
          <div className="flex shrink-0 gap-1.5 max-[359px]:grid max-[359px]:grid-cols-3">
            {pickCell('home')}
            {pickCell('away')}
            {market.marketCount > 0 ? (
              <LocalizedLink
                to="zambePongZambeBetMatch"
                params={{ id: tournamentId, matchId }}
                data-testid={`market-more-${matchId}`}
                aria-label={t('zambebet.markets.moreLabel', { count: market.marketCount, home: market.homeTeam.name, away: market.awayTeam.name })}
                className={cn(cellClass, 'border-sun/40 bg-sun/10 text-sun hover:border-sun hover:bg-sun/20 active:scale-[0.95]')}
              >
                <span className="text-[0.7rem] font-semibold leading-none text-sun/80">{t('zambebet.markets.other')}</span>
                <span className="flex items-center text-sm font-semibold leading-none tabular-nums">
                  +{market.marketCount}
                  <ChevronRight className="-mr-1 size-3.5" aria-hidden="true" />
                </span>
              </LocalizedLink>
            ) : (
              <span aria-hidden="true" className={cn(cellClass, 'border-paper/10 text-paper/30')}>
                <span className="text-[0.7rem] font-semibold leading-none">{t('zambebet.markets.other')}</span>
                <span className="text-sm leading-none">–</span>
              </span>
            )}
          </div>
        )}
      </div>

      {reason && !isCompleted && <p data-testid={`market-reason-${matchId}`} className="mt-2 font-body text-xs text-paper/60">{reason}</p>}
      {market.totalStaked > 0 && !isCompleted && (
        <p className="mt-1.5 font-body text-xs text-paper/45">{t('zambebet.markets.staked', { count: market.totalStaked })}</p>
      )}
    </article>
  )
}
